import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 1.4707, lng: 103.6245 }; // Johor, Malaysia

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const PropertyMap = ({ address, propertyName, latitude, longitude, height = '300px' }) => {
  const [coords, setCoords] = useState(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  const [geocoding, setGeocoding] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY || '',
  });

  const geocodeAddress = useCallback(() => {
    if (!isLoaded || !address || coords) return;
    setGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setCoords({ lat: loc.lat(), lng: loc.lng() });
      } else {
        setError('Location not found on map');
      }
      setGeocoding(false);
    });
  }, [isLoaded, address, coords]);

  useEffect(() => {
    if (latitude && longitude) {
      setCoords({ lat: latitude, lng: longitude });
      return;
    }
    geocodeAddress();
  }, [latitude, longitude, geocodeAddress]);

  if (!address && !coords) return null;

  if (loadError) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100"
        style={{ height }}
        data-testid="property-map-error"
      >
        <p className="text-sm text-gray-400">Failed to load Google Maps</p>
      </div>
    );
  }

  if (!isLoaded || geocoding) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100"
        style={{ height }}
        data-testid="property-map-loading"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#4E50FF] mx-auto mb-2" />
          <p className="text-sm text-gray-400">Loading map...</p>
        </div>
      </div>
    );
  }

  if (error || !coords) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100"
        style={{ height }}
        data-testid="property-map-error"
      >
        <p className="text-sm text-gray-400">{error || 'No map available'}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height }} data-testid="property-map">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={coords}
        zoom={15}
        options={mapOptions}
      >
        <Marker
          position={coords}
          onClick={() => setShowInfo(true)}
        />
        {showInfo && (
          <InfoWindow position={coords} onCloseClick={() => setShowInfo(false)}>
            <div style={{ maxWidth: 200 }}>
              <strong style={{ fontSize: 14 }}>{propertyName}</strong>
              {address && <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{address}</p>}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default PropertyMap;
